import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AgeRestrictedError,
  ConnectAccessDeniedError,
  EulaRequiredError,
  SubscriptionInactiveError,
} from '@/shared/domain'
import type { LoadConnectUnreadTotal } from '@/shared/domain/use-cases'
import { connectUnreadTotalKey } from '@/shared/infra/query'
import { useCurrentPersona, useOrgContext } from '@/shared/ui/hooks'
import { NavBadgeContext } from './nav-badge-context'
import { CONNECT_UNREAD_CHANGED_EVENT } from './connect-unread-event'

const CONNECT_NAV_ID = 'connect-messages'
const POLL_INTERVAL_MS = 60_000

const BADGE_PERSONAS = new Set(['club-manager', 'outfitter-owner'])

function isTerminalConnectError(err: unknown): boolean {
  return (
    err instanceof EulaRequiredError ||
    err instanceof AgeRestrictedError ||
    err instanceof ConnectAccessDeniedError ||
    err instanceof SubscriptionInactiveError
  )
}

type NavBadgeProviderProps = {
  onLoadConnectUnreadTotal: LoadConnectUnreadTotal
  children: ReactNode
}

export function NavBadgeProvider({
  onLoadConnectUnreadTotal,
  children,
}: NavBadgeProviderProps) {
  const { orgId, orgType } = useOrgContext()
  const { persona } = useCurrentPersona()
  const queryClient = useQueryClient()
  const [accessBlocked, setAccessBlocked] = useState(false)

  const resolvedOrgType = orgType === 'club' || orgType === 'outfitter' ? orgType : null

  const eligible = BADGE_PERSONAS.has(persona) && Boolean(orgId) && resolvedOrgType !== null
  const enabled = eligible && !accessBlocked

  useEffect(() => {
    setAccessBlocked(false)
  }, [orgId, orgType])

  const queryKey = useMemo(
    () => connectUnreadTotalKey(orgId ?? '', resolvedOrgType ?? 'club'),
    [orgId, resolvedOrgType],
  )

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      try {
        return await onLoadConnectUnreadTotal({
          orgId: orgId as string,
          orgType: resolvedOrgType as 'club' | 'outfitter',
        })
      } catch (err) {
        if (isTerminalConnectError(err)) setAccessBlocked(true)
        throw err
      }
    },
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  })

  useEffect(() => {
    if (!eligible) return
    const onUnreadChanged = () => {
      setAccessBlocked(false)
      void queryClient.invalidateQueries({ queryKey })
    }
    window.addEventListener(CONNECT_UNREAD_CHANGED_EVENT, onUnreadChanged)
    return () => window.removeEventListener(CONNECT_UNREAD_CHANGED_EVENT, onUnreadChanged)
  }, [eligible, queryClient, queryKey])

  const connectUnread = enabled ? query.data?.total ?? 0 : 0

  const value = useMemo(
    () => ({ badgesByNavId: { [CONNECT_NAV_ID]: connectUnread } }),
    [connectUnread],
  )

  return <NavBadgeContext.Provider value={value}>{children}</NavBadgeContext.Provider>
}
